import { isDefined } from '@/utils/general'
import { DeleteActionCellRenderer } from '@components/DriverTracking/Dwell/components/DeleteActionCell'
import { DwellOverrideCell } from '@components/DriverTracking/Dwell/components/DwellTerminalGrid/DwellOverrideCell'
import { parseTimeStringToMinutes } from '@components/_SiteWide/Form/HourMinuteInput'
import { NumberCellEditor } from '@components/_SiteWide/Grid/CellEditors/NumberCellEditor'
import { durationFormatter } from '@utils/cell_formatter'
import { getDateAtTimeString } from '@utils/intl/dateTimeFormatters'
import type { ColDef } from 'ag-grid-community'

const MAX_MINUTES = 60 * 12

const durationColumn = (allowNull: boolean): ColDef => ({
  editable: true,
  enableRowGroup: false,
  filter: 'agNumberColumnFilter',
  sortable: true,
  cellEditor: NumberCellEditor,
  valueFormatter: ({ value }) => (isDefined(value) ? durationFormatter(value) : ''),
  valueParser: ({ newValue }) => {
    return parseTimeStringToMinutes(newValue) || newValue
  },
  valueSetter: (params) => {
    if (params.newValue < 0) return false
    if (params.newValue > MAX_MINUTES + 1) return false
    if (isDefined(params.newValue)) {
      params.data[params.colDef.field ?? ''] = parseInt(params.newValue, 0)
      return true
    }
    if (allowNull) {
      params.data[params.colDef.field ?? ''] = null
      return true
    }
    return false
  },
})

export const getColumnDefs = (
  terminalNames: string[],
  productGroups: string[],
  createHandleOverride: unknown,
  createHandleDeleteOverride: unknown,
  createHandleDelete: unknown,
  hasEdits: boolean
): (
  | { headerName: string; children: ColDef[]; marryChildren: boolean }
  | {
      suppressFiltersToolPanel: boolean
      headerName: string
      suppressColumnsToolPanel: boolean
      children: ColDef[]
      marryChildren: boolean
      suppressMovable: boolean
    }
)[] => {
  const terminalInformationColumns: ColDef[] = [
    {
      headerName: 'Terminal',
      field: 'terminal_name',
      editable: (params) => params.data.$inserted,
      cellEditor: 'agRichSelectCellEditor',
      cellEditorParams: {
        values: terminalNames,
      },
    },
    {
      headerName: 'Product Group',
      field: 'product_group',
      editable: (params) => params.data.$inserted,
      cellEditor: 'agRichSelectCellEditor',
      valueFormatter: (params) => (params.value === 'default' ? '' : params.value),
      cellEditorParams: {
        values: productGroups,
      },
    },
    {
      headerName: 'Market',
      field: 'market',
    },
    {
      headerName: 'Supply Zone',
      field: 'supply_zone',
    },
  ]
  const dwellConfigurationColumns: ColDef[] = [
    {
      headerName: 'Set Dwell',
      field: 'set_dwell',
      headerTooltip: 'Time spent at the location. Will update projected ETAs for drivers stopping at this location.',
      ...durationColumn(false),
    },
    {
      headerName: 'Payroll Override',
      field: 'payroll_override',
      headerTooltip: 'Time spent at the location for payroll purposes',
      ...durationColumn(true),
    },
    {
      headerName: 'Temp Override',
      headerTooltip:
        'Temporary override of the set dwell time. Will update projected ETAs for drivers stopping at this location while active.',
      enableRowGroup: false,
      sortable: false,
      filter: false,
      cellRenderer: (params) => (
        <DwellOverrideCell
          params={params}
          createHandleOverride={createHandleOverride}
          createHandleDeleteOverride={createHandleDeleteOverride}
          hasEdits={hasEdits}
          timezone={params.data?.override_expiry_local}
        />
      ),
    },
    {
      headerName: 'Override Expiration',
      field: 'override_expiry',
      headerTooltip: 'Time when the temporary override will expire.',
      enableRowGroup: false,
      valueGetter: (params) =>
        params.data?.override_expiry
          ? getDateAtTimeString(params.data.override_expiry, params.data.override_expiry_local)
          : '',
    },
    {
      headerName: 'Actions',
      enableRowGroup: false,
      sortable: false,
      filter: false,
      cellRenderer: (params) => (
        <DeleteActionCellRenderer params={params} createHandleDelete={createHandleDelete} hasEdits={hasEdits} />
      ),
    },
  ]

  return [
    { headerName: 'Terminal Information', marryChildren: true, children: terminalInformationColumns },
    {
      headerName: 'Dwell Configuration',
      marryChildren: true,
      children: dwellConfigurationColumns,
      suppressMovable: true,
      suppressColumnsToolPanel: true,
      suppressFiltersToolPanel: true,
    },
  ]
}
